import React, { useEffect, useContext, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//functions
import {
  _t,
  getCookie,
  tableLoading,
  customStyle,
} from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";
import { useCustomHooks } from "../../../../../functions/Hooks";

//3rd party packages
import { Helmet } from "react-helmet";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

//pages & includes
import ManageSidebar from "../ManageSidebar";

//context consumer
import { RestaurantContext } from "../../../../../contexts/Restaurant";
import { useGeneralHooks } from "../../../../../hooks/generalHooks";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";

const animatedComponents = makeAnimated();

const StockReceivingDeptAction = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { roundOffStock, qtyAmountPoints } = useCustomHooks();
  const { issueQtyLock } = useGeneralHooks();

  const {
    loading,
    setLoading,
    branchForSearch,
    getBranch,
  } = useContext(RestaurantContext);

  const [formData, setFormData] = useState({
    document_no: "",
    ref_identity: "",
    requisition_doc_identity: "",
    issuance_doc_identity: "",
    branch: null,
    fdept: null,
    tdept: null,
    remarks: "",
    is_post: 0,
  });

  const [newRow, setNewRow] = useState([]);
  const [documentDate, setDocumentDate] = useState(new Date());
  const [branchDepartments, setBranchDepartments] = useState([]);
  const [loadingDept, setLoadingDept] = useState(false);
  const [storeReqDoc, setStoreReqDoc] = useState([]);

  // Fetch branch-wise departments
  const getBranchWiseDepartments = async (branchId) => {
    if (!branchId) {
      setBranchDepartments([]);
      return;
    }
    setLoadingDept(true);
    const url = BASE_URL + "/settings/branch-wise-department/" + branchId;
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      const depts = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data?.departments)
        ? res.data.departments
        : [];
      setBranchDepartments(depts);
    } catch (error) {
      console.log("Error fetching branch departments:", error);
      setBranchDepartments([]);
    } finally {
      setLoadingDept(false);
    }
  };

  const populateEditForm = (data) => {
    const branch =
      data.branch ||
      (branchForSearch &&
        branchForSearch.find((b) => b.id === data.branch_id)) ||
      (data.branch_id
        ? { id: data.branch_id, name: data.branch_name }
        : null);

    const fdept =
      data.from_department ||
      data.from_dept ||
      (data.from_dept_id
        ? { id: data.from_dept_id, name: data.from_dept_name }
        : null);

    const tdept =
      data.to_department ||
      data.to_dept ||
      (data.to_dept_id
        ? { id: data.to_dept_id, name: data.to_dept_name }
        : null);

    setFormData({
      ...formData,
      is_post: data.is_post,
      document_no: data.document_identity || data.document_no || "",
      branch: branch,
      fdept: fdept,
      tdept: tdept,
      ref_identity: {
        id: null,
        document_identity: data.issuance_doc_identity || data.ref_identity,
      },
      requisition_doc_identity: data?.requisition_doc_identity || "",
      issuance_doc_identity: data?.issuance_doc_identity || "",
      remarks: data.remarks || "",
    });

    setDocumentDate(new Date(data.document_date || data.date));

    const branchId = data.branch?.id || data.branch_id;
    if (branchId) {
      getBranchWiseDepartments(branchId);
    }

    const details =
      data.stock_receiving_department_detail ||
      data.stock_receiving_dept_detail ||
      data.stock_issuance_department_detail ||
      data.stock_acceptance_detail ||
      data.stock_issuance_detail ||
      data.details ||
      [];

    if (details.length > 0) {
      const rowArr = details.map((item) => ({
        item: { id: item.item_id, name: item.item_name },
        unit: { id: item.unit_id, name: item.unit_name },
        item_id: item.item_id,
        item_name: item.item_name,
        unit_name: item.unit_name,
        unit_id: item.unit_id,
        qty: roundOffStock(item.qty),
        remarks: item.item_remarks || item.remarks || "",
        received_qty: roundOffStock(item.qty),
        requisition_qty: roundOffStock(item.requisition_qty || 0),
        issued_qty: roundOffStock(item.issued_qty || item.qty),
        base_unit: item.base_unit || item.unit_id || "",
        base_qty: item.base_qty || "",
        conversion_qty: item.conversion_qty || 1,
        cost_price: parseFloat(item.cost_price || 0),
      }));
      setNewRow(rowArr);
    }
  };

  const fetchReceivingItem = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        BASE_URL + "/settings/stock-receiving-dept/" + parseInt(id),
        { headers: { Authorization: `Bearer ${getCookie()}` } }
      );
      if (res && res.data) {
        populateEditForm(res.data);
      }
    } catch (err) {
      console.log("Error fetching receiving item:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!branchForSearch && getBranch) {
      getBranch();
    }
    if (id) {
      fetchReceivingItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch Issuance Docs for Selected Depts
  const getStoreReqDoc = async (toDeptId, fromDeptId) => {
    const fId = fromDeptId || formData.fdept?.id;
    const tId = toDeptId || formData.tdept?.id;
    if (!fId || !tId) return;

    try {
      const url = `${BASE_URL}/settings/get-stock-issuance-dept-list`;
      const payload = {
        branch_id: formData.branch?.id,
        from_dept_id: fId,
        to_dept_id: tId,
      };

      const res = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });

      if (res && res.data) {
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.stock_issuances_dept)
          ? res.data.stock_issuances_dept
          : [];
        setStoreReqDoc(list);
      }
    } catch (error) {
      console.log("check error", error);
    }
  };

  // Fetch Items inside selected Issuance doc
  const getStoreReqItems = async (ref_doc) => {
    if (!ref_doc || !ref_doc.id) return;

    try {
      const url = `${BASE_URL}/settings/get-stock-issuance-dept-details/${ref_doc.id}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });

      const resData = res?.data?.data || res?.data;
      if (resData) {
        const details =
          resData.stock_issuance_department_detail ||
          resData.stock_issuance_dept_detail ||
          resData.stock_issuance_detail ||
          resData.stock_acceptance_detail ||
          resData.details ||
          (Array.isArray(resData) ? resData : []);

        setFormData((prev) => ({
          ...prev,
          ref_identity: ref_doc,
          requisition_doc_identity:
            resData.ref_identity || resData.requisition_doc_identity || "",
          issuance_doc_identity:
            resData.document_identity ||
            resData.issuance_doc_identity ||
            ref_doc.document_identity ||
            "",
        }));

        const rowArr = details.map((item) => {
          const issuedQty =
            parseFloat(item.qty) > 0
              ? item.qty
              : item.issued_qty || item.qty || 0;
          const reqQty =
            item.requisition_qty !== undefined && item.requisition_qty !== null
              ? item.requisition_qty
              : issuedQty;

          return {
            ...item,
            item_id: item.item_id || item.item?.id,
            item_name: item.item_name || item.item?.name,
            unit_id: item.unit_id || item.unit?.id,
            unit_name: item.unit_name || item.unit?.name,
            cost_price: parseFloat(item.cost_price || 0),
            max_qty: Number(issuedQty),
            remarks: item?.item_remarks || item?.remarks || "",
            issued_qty: roundOffStock(issuedQty),
            received_qty: roundOffStock(issuedQty),
            requisition_qty: roundOffStock(reqQty),
            stock_qty: roundOffStock(item.stock_qty || 0),
            base_unit: item.base_unit || item.unit_id || item.unit?.id || "",
            base_qty:
              item.base_qty !== undefined && item.base_qty !== null
                ? item.base_qty
                : issuedQty,
            conversion_qty: item.conversion_qty || 1,
          };
        });

        setNewRow(rowArr);
      }
    } catch (error) {
      console.log("check error", error);
    }
  };

  // User can less qty, but not exceed issued_qty
  const handleItemQty = (index, e) => {
    const val = e.target.value;
    if (val === "" || val === null) {
      newRow[index].received_qty = "";
      setNewRow([...newRow]);
      return;
    }

    const entered = parseFloat(val);
    const issued = parseFloat(newRow[index].issued_qty);

    if (!isNaN(issued) && entered > issued) {
      ToastError({
        title: _t(t("Your qty should not be greater than the issued qty.")),
      });
    } else {
      newRow[index].received_qty = val;
      setNewRow([...newRow]);
    }
  };

  // Branch Selection
  const handleSetBranch = (branch) => {
    setFormData((prev) => ({
      ...prev,
      branch: branch || null,
      fdept: null,
      tdept: null,
      ref_identity: "",
      requisition_doc_identity: "",
      issuance_doc_identity: "",
    }));
    setBranchDepartments([]);
    setStoreReqDoc([]);
    setNewRow([]);
    if (branch && branch.id) {
      getBranchWiseDepartments(branch.id);
    }
  };

  // From Dept Selection
  const handleSetFromDept = (fdept) => {
    setFormData((prev) => ({
      ...prev,
      fdept: fdept || null,
      tdept: null,
      ref_identity: "",
      requisition_doc_identity: "",
      issuance_doc_identity: "",
    }));
    setStoreReqDoc([]);
    setNewRow([]);
  };

  // To Dept Selection
  const handleSetToDept = (tdept) => {
    setFormData((prev) => ({
      ...prev,
      tdept: tdept || null,
      ref_identity: "",
      requisition_doc_identity: "",
      issuance_doc_identity: "",
    }));
    setStoreReqDoc([]);
    setNewRow([]);
    if (tdept && tdept.id && formData.fdept?.id) {
      getStoreReqDoc(tdept.id, formData.fdept.id);
    }
  };

  // Document Ref Selection
  const handleSetDocId = (ref_identity) => {
    setFormData((prev) => ({ ...prev, ref_identity: ref_identity || "" }));
    if (ref_identity) {
      getStoreReqItems(ref_identity);
    } else {
      setNewRow([]);
    }
  };

  const handleRemarks = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, remarks: value }));
  };

  const handleSave = async () => {
    const d = new Date(documentDate);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    let isQtyOverflow = false;
    const itemDetails = [];

    newRow.forEach((row) => {
      const recQty = parseFloat(row.received_qty);
      const issQty = parseFloat(row.issued_qty || 0);
      if (recQty > issQty) {
        isQtyOverflow = true;
      }
      const conversion = parseFloat(row.conversion_qty || 1) || 1;
      const baseQty = recQty * conversion;

      itemDetails.push({
        qty: recQty,
        requisition_qty: parseFloat(row.requisition_qty || 0),
        issued_qty: issQty,
        item_id: row.item_id,
        item_name: row.item_name,
        unit_id: row.unit_id,
        unit_name: row.unit_name,
        base_unit: row.base_unit || row.unit_id,
        base_qty: baseQty,
        conversion_qty: conversion,
        cost_price: parseFloat(row.cost_price || 0),
        item_remarks: row.remarks || row.item_remarks || "",
      });
    });

    if (isQtyOverflow) {
      ToastError({
        title: _t(t("Your qty should not be greater than the issued qty.")),
      });
      return;
    }

    if (newRow.length === itemDetails.length) {
      setLoading(true);
      const payload = {
        branch_id: formData.branch?.id,
        from_dept_id: formData.fdept?.id,
        to_dept_id: formData.tdept?.id,
        document_date: date,
        requisition_doc_identity: formData.requisition_doc_identity || "",
        issuance_doc_identity: formData.issuance_doc_identity || "",
        remarks: formData.remarks || "",
        items: itemDetails,
      };

      const method = id ? "put" : "post";
      const url = BASE_URL + "/settings/stock-receiving-dept" + (id ? `/${id}` : "");
      try {
        await axios[method](url, payload, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });
        setLoading(false);
        ToastSuccess({
          title: _t(
            t(
              id
                ? "Stock receiving has been updated"
                : "Stock receiving has been saved"
            )
          ),
        });
        history.push("/dashboard/manage/stock/stock-receiving-dept");
      } catch (error) {
        console.log("check error==>", error);
        setLoading(false);
        if (error.response?.data?.message) {
          ToastError({ title: _t(t(error.response.data.message)) });
          return;
        }
        ToastError({ title: _t(t("Please try again later")) });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      formData.branch !== null &&
      formData.fdept !== null &&
      formData.tdept !== null &&
      documentDate !== null &&
      Boolean(formData?.requisition_doc_identity || formData?.issuance_doc_identity)
    ) {
      if (newRow.length > 0) {
        handleSave();
      } else {
        ToastError({ title: _t(t("Please select doc with item")) });
      }
    } else {
      ToastError({ title: _t(t("Please fill all the required fields")) });
    }
  };

  const pageTitle = id
    ? formData?.is_post
      ? _t(t("View Stock Receiving Dept"))
      : _t(t("Edit Stock Receiving Dept"))
    : _t(t("Add Stock Receiving Dept"));

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    {loading === true ? (
                      tableLoading()
                    ) : (
                      <div key="smtp-form">
                        <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                          <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                            <ul className="t-list fk-breadcrumb">
                              <li className="fk-breadcrumb__list">
                                <span className="t-link fk-breadcrumb__link text-capitalize">
                                  {pageTitle}
                                </span>
                              </li>
                            </ul>
                          </div>
                          <div className="col-md-6 col-lg-7">
                            <div className="row gx-3 align-items-center"></div>
                          </div>
                        </div>

                        <form
                          className="row card p-2 mx-3 mb-5 table-text"
                          onSubmit={handleSubmit}
                          autoComplete="off"
                        >
                          <div className="col-12">
                            <div className="row">
                              <div className="form-group col-3 mt-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="document_no"
                                    className="control-label"
                                  >
                                    {_t(t("Document no"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <input
                                    readOnly
                                    type="text"
                                    className="form-control table-text"
                                    id="document_no"
                                    name="document_no"
                                    value={formData.document_no || ""}
                                    placeholder="Auto no"
                                    required
                                  />
                                </div>
                              </div>

                              <div className="form-group col-3 text-right mt-3">
                                <div className="mb-2 text-left">
                                  <label
                                    htmlFor="transfer_date"
                                    className="control-label"
                                  >
                                    {_t(t("Document Date"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <DatePicker
                                    selected={documentDate}
                                    onChange={(date) => setDocumentDate(date)}
                                    peekNextMonth
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    className="form-control table-text"
                                    placeholderText={_t(t("Document date"))}
                                    shouldCloseOnSelect={false}
                                    disabled={formData.is_post}
                                  />
                                </div>
                              </div>

                              <div className="form-group mt-3 col-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="branch"
                                    className="control-label"
                                  >
                                    {_t(t("Branch"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={branchForSearch ? branchForSearch : []}
                                  components={animatedComponents}
                                  getOptionLabel={(option) => option?.name || option?.branch_name || ""}
                                  getOptionValue={(option) => option?.id || ""}
                                  value={formData.branch || null}
                                  classNamePrefix="select"
                                  onChange={handleSetBranch}
                                  maxMenuHeight="200px"
                                  isDisabled={id || formData.is_post}
                                  styles={customStyle}
                                  placeholder={
                                    _t(t("Please select a branch")) + ".."
                                  }
                                />
                              </div>

                              <div className="form-group mt-3 col-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="fdept"
                                    className="control-label"
                                  >
                                    {_t(t("From Department"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={Array.isArray(branchDepartments) ? branchDepartments : []}
                                  components={animatedComponents}
                                  getOptionLabel={(option) => option?.name || option?.dept_name || ""}
                                  getOptionValue={(option) => option?.id || ""}
                                  value={formData.fdept || null}
                                  classNamePrefix="select"
                                  onChange={handleSetFromDept}
                                  maxMenuHeight="200px"
                                  isLoading={loadingDept}
                                  isDisabled={!formData.branch || id || formData.is_post}
                                  styles={customStyle}
                                  placeholder={
                                    !formData.branch
                                      ? _t(t("Please select branch first"))
                                      : loadingDept
                                      ? _t(t("Loading..."))
                                      : _t(t("Please select from dept")) + ".."
                                  }
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="form-group mt-2 col-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="tdept"
                                    className="control-label"
                                  >
                                    {_t(t("To Department"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={
                                    Array.isArray(branchDepartments)
                                      ? branchDepartments.filter(
                                          (item) => item && item.id !== formData?.fdept?.id
                                        )
                                      : []
                                  }
                                  components={animatedComponents}
                                  getOptionLabel={(option) => option?.name || option?.dept_name || ""}
                                  getOptionValue={(option) => option?.id || ""}
                                  value={formData.tdept || null}
                                  classNamePrefix="select"
                                  onChange={handleSetToDept}
                                  maxMenuHeight="200px"
                                  isDisabled={!formData.fdept || id || formData.is_post}
                                  styles={customStyle}
                                  placeholder={
                                    !formData.fdept
                                      ? _t(t("Please select from dept first"))
                                      : _t(t("Please select to dept")) + ".."
                                  }
                                />
                              </div>

                              <div className="form-group mt-2 col-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="ref_identity"
                                    className="control-label"
                                  >
                                    {_t(t("Select Document Ref"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={Array.isArray(storeReqDoc) ? storeReqDoc : []}
                                  components={animatedComponents}
                                  getOptionLabel={(option) =>
                                    (option?.document_identity || "") +
                                    (option?.ref_identity
                                      ? " (" + option?.ref_identity + ")"
                                      : "")
                                  }
                                  getOptionValue={(option) => option?.id || ""}
                                  value={formData.ref_identity || null}
                                  classNamePrefix="select"
                                  onChange={handleSetDocId}
                                  isDisabled={!formData.tdept || formData.is_post}
                                  maxMenuHeight="200px"
                                  styles={customStyle}
                                  placeholder={
                                    !formData.tdept
                                      ? _t(t("Please select to dept first"))
                                      : _t(t("Please select doc")) + ".."
                                  }
                                />
                              </div>

                              <div className="form-group mt-2 col-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="request_ref"
                                    className="control-label"
                                  >
                                    {_t(t("Request Ref"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <input
                                  readOnly
                                  type="text"
                                  className="form-control table-text"
                                  value={formData.issuance_doc_identity || ""}
                                  placeholder="Auto Ref"
                                />
                              </div>

                              <div className="form-group mt-2 col-3">
                                <div className="mb-2">
                                  <label
                                    htmlFor="remarks"
                                    className="control-label"
                                  >
                                    {_t(t("Remarks"))}
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <textarea
                                    className="form-control table-text"
                                    id="remarks"
                                    name="remarks"
                                    onChange={handleRemarks}
                                    value={formData.remarks || ""}
                                    placeholder="Remarks"
                                    style={{ height: "33px" }}
                                    disabled={formData.is_post}
                                  />
                                </div>
                              </div>
                            </div>

                            <div
                              className="table-responsive mt-3 pr-1"
                              style={{ maxHeight: "23rem", overflow: "auto" }}
                            >
                              <table className="table sticky-table-header table-bordered-sm table-hover">
                                <thead className="align-middle">
                                  <tr>
                                    <th
                                      style={{ minWidth: "150px" }}
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border table_child"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Item Name"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Requisition Qty"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Issued Qty"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Received Qty"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Unit"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Remarks"))}
                                      </span>
                                    </th>
                                  </tr>
                                </thead>
                                {newRow.length > 0 && (
                                  <tbody className="align-middle">
                                    {newRow.map((item, index) => (
                                      <tr className="align-middle" key={index}>
                                        <td className="table-text text-capitalize align-middle text-center">
                                          {item?.item_name}
                                        </td>
                                        <td className="table-text text-capitalize align-middle text-center">
                                          {item.requisition_qty}
                                        </td>
                                        <td className="table-text text-capitalize align-middle text-center">
                                          {item.issued_qty}
                                        </td>
                                        <td className="table-text text-capitalize align-middle text-center">
                                          <input
                                            disabled={
                                              formData.is_post || issueQtyLock
                                            }
                                            type="number"
                                            step={qtyAmountPoints()}
                                            min="0"
                                            onKeyDown={(evt) =>
                                              [
                                                "e",
                                                "E",
                                                "+",
                                                "-",
                                                "ArrowUp",
                                                "ArrowDown",
                                              ].includes(evt.key) &&
                                              evt.preventDefault()
                                            }
                                            onWheel={(e) => e.target.blur()}
                                            className="form-control text-center table-Input table-text"
                                            onChange={(e) =>
                                              handleItemQty(index, e)
                                            }
                                            value={
                                              item?.received_qty !== undefined &&
                                              item?.received_qty !== null
                                                ? item.received_qty
                                                : ""
                                            }
                                            placeholder="Received Qty"
                                            required
                                          />
                                        </td>
                                        <td className="table-text text-capitalize align-middle text-center">
                                          {item?.unit_name}
                                        </td>
                                        <td className="table-text text-capitalize align-middle text-center">
                                          {item?.remarks || "-"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                )}
                              </table>
                            </div>

                            <div className="d-flex mt-2 justify-content-end align-items-center">
                              <div className="col-3 d-flex justify-content-end">
                                <button
                                  type="button"
                                  className="btn rounded hov-btn mr-3"
                                  onClick={() =>
                                    history.push(
                                      "/dashboard/manage/stock/stock-receiving-dept"
                                    )
                                  }
                                >
                                  <i
                                    className="fa fa-arrow-left"
                                    aria-hidden="true"
                                  ></i>{" "}
                                  {_t(t("Back"))}
                                </button>
                                {!formData?.is_post && (
                                  <button
                                    disabled={loading}
                                    type="submit"
                                    className="btn btn-primary rounded"
                                  >
                                    <i
                                      className="fa fa-floppy-o"
                                      aria-hidden="true"
                                    ></i>{" "}
                                    {_t(t("Save"))}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default StockReceivingDeptAction;
